import { useCallback, useEffect, useState } from 'react';
import {
  CollectionView,
  ItemSort,
  LocationSort,
} from '../features/collection/types';

export type CardSize = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9;
export const CARD_SIZES: readonly CardSize[] = [1, 2, 3, 4, 5, 6, 7, 8, 9];
export interface UiPreferences {
  cardSize: CardSize;
  removeMargins: boolean;
  itemView: CollectionView;
  locationView: CollectionView;
  itemSort: ItemSort;
  locationSort: LocationSort;
}

export const UI_PREFERENCES_KEY = 'qkindex.ui.preferences.v5';
export const LEGACY_UI_PREFERENCES_KEY = 'qkindex.ui.preferences.v4';
export const DEFAULT_UI_PREFERENCES: UiPreferences = {
  cardSize: 5,
  removeMargins: false,
  itemView: 'grid',
  locationView: 'list',
  itemSort: 'edited-desc',
  locationSort: 'name-asc',
};
export const CARD_SIZE_WIDTHS: Record<CardSize, number> = {
  1: 104,
  2: 120,
  3: 136,
  4: 152,
  5: 168,
  6: 184,
  7: 204,
  8: 224,
  9: 256,
};

export interface DisplaySizeProfile {
  itemListRowHeight: number;
  itemListImageSize: number;
  itemListGap: number;
  locationListPaddingY: number;
  locationListDescriptionLines: number;
  locationGridMinWidth: number;
  locationGridMinHeight: number;
  locationGridPadding: number;
  locationGridContentGap: number;
  gridGap: number;
  locationGridDescriptionLines: number;
}

export const DISPLAY_SIZE_PROFILES: Record<CardSize, DisplaySizeProfile> = {
  1: {
    itemListRowHeight: 48,
    itemListImageSize: 48,
    itemListGap: 0.75,
    locationListPaddingY: 0.25,
    locationListDescriptionLines: 1,
    locationGridMinWidth: 104,
    locationGridMinHeight: 96,
    locationGridPadding: 1,
    locationGridContentGap: 0.5,
    gridGap: 0.5,
    locationGridDescriptionLines: 1,
  },
  2: {
    itemListRowHeight: 56,
    itemListImageSize: 56,
    itemListGap: 0.75,
    locationListPaddingY: 0.25,
    locationListDescriptionLines: 1,
    locationGridMinWidth: 120,
    locationGridMinHeight: 104,
    locationGridPadding: 1,
    locationGridContentGap: 0.75,
    gridGap: 0.75,
    locationGridDescriptionLines: 1,
  },
  3: {
    itemListRowHeight: 64,
    itemListImageSize: 64,
    itemListGap: 0.75,
    locationListPaddingY: 0.25,
    locationListDescriptionLines: 1,
    locationGridMinWidth: 136,
    locationGridMinHeight: 112,
    locationGridPadding: 1.25,
    locationGridContentGap: 0.75,
    gridGap: 1,
    locationGridDescriptionLines: 2,
  },
  4: {
    itemListRowHeight: 72,
    itemListImageSize: 72,
    itemListGap: 1,
    locationListPaddingY: 0.5,
    locationListDescriptionLines: 1,
    locationGridMinWidth: 152,
    locationGridMinHeight: 120,
    locationGridPadding: 1.5,
    locationGridContentGap: 0.75,
    gridGap: 1,
    locationGridDescriptionLines: 2,
  },
  5: {
    itemListRowHeight: 80,
    itemListImageSize: 80,
    itemListGap: 1,
    locationListPaddingY: 0.75,
    locationListDescriptionLines: 2,
    locationGridMinWidth: 168,
    locationGridMinHeight: 132,
    locationGridPadding: 1.75,
    locationGridContentGap: 1,
    gridGap: 1,
    locationGridDescriptionLines: 3,
  },
  6: {
    itemListRowHeight: 88,
    itemListImageSize: 88,
    itemListGap: 1.5,
    locationListPaddingY: 1,
    locationListDescriptionLines: 2,
    locationGridMinWidth: 180,
    locationGridMinHeight: 144,
    locationGridPadding: 2,
    locationGridContentGap: 1,
    gridGap: 1,
    locationGridDescriptionLines: 3,
  },
  7: {
    itemListRowHeight: 96,
    itemListImageSize: 96,
    itemListGap: 1.5,
    locationListPaddingY: 1.25,
    locationListDescriptionLines: 2,
    locationGridMinWidth: 208,
    locationGridMinHeight: 160,
    locationGridPadding: 2.25,
    locationGridContentGap: 1.25,
    gridGap: 1,
    locationGridDescriptionLines: 4,
  },
  8: {
    itemListRowHeight: 104,
    itemListImageSize: 104,
    itemListGap: 2,
    locationListPaddingY: 1.5,
    locationListDescriptionLines: 3,
    locationGridMinWidth: 240,
    locationGridMinHeight: 176,
    locationGridPadding: 2.5,
    locationGridContentGap: 1.5,
    gridGap: 1,
    locationGridDescriptionLines: 5,
  },
  9: {
    itemListRowHeight: 112,
    itemListImageSize: 112,
    itemListGap: 2,
    locationListPaddingY: 2,
    locationListDescriptionLines: 3,
    locationGridMinWidth: 280,
    locationGridMinHeight: 200,
    locationGridPadding: 3,
    locationGridContentGap: 1.5,
    gridGap: 1,
    locationGridDescriptionLines: 6,
  },
};

const itemSorts: ItemSort[] = [
  'edited-desc',
  'created-desc',
  'created-asc',
  'keywords-asc',
  'keywords-desc',
  'location-asc',
  'location-desc',
  'quantity-desc',
  'quantity-asc',
];
const locationSorts: LocationSort[] = [
  'name-asc',
  'name-desc',
  'edited-desc',
  'created-desc',
  'items-desc',
  'items-asc',
];

export function normalizeUiPreferences(value: unknown): UiPreferences {
  if (!value || typeof value !== 'object') return DEFAULT_UI_PREFERENCES;
  const candidate = value as Partial<UiPreferences>;
  return {
    cardSize: CARD_SIZES.includes(candidate.cardSize as CardSize)
      ? (candidate.cardSize as CardSize)
      : DEFAULT_UI_PREFERENCES.cardSize,
    removeMargins:
      typeof candidate.removeMargins === 'boolean'
        ? candidate.removeMargins
        : DEFAULT_UI_PREFERENCES.removeMargins,
    itemView:
      candidate.itemView === 'grid' || candidate.itemView === 'list'
        ? candidate.itemView
        : DEFAULT_UI_PREFERENCES.itemView,
    locationView:
      candidate.locationView === 'grid' || candidate.locationView === 'list'
        ? candidate.locationView
        : DEFAULT_UI_PREFERENCES.locationView,
    itemSort: itemSorts.includes(candidate.itemSort as ItemSort)
      ? (candidate.itemSort as ItemSort)
      : DEFAULT_UI_PREFERENCES.itemSort,
    locationSort: locationSorts.includes(candidate.locationSort as LocationSort)
      ? (candidate.locationSort as LocationSort)
      : DEFAULT_UI_PREFERENCES.locationSort,
  };
}

function readPreferences() {
  try {
    const persisted = localStorage.getItem(UI_PREFERENCES_KEY);
    if (persisted !== null)
      return normalizeUiPreferences(JSON.parse(persisted));

    const legacy = localStorage.getItem(LEGACY_UI_PREFERENCES_KEY);
    if (legacy === null) return DEFAULT_UI_PREFERENCES;
    const candidate = JSON.parse(legacy);
    if (!candidate || typeof candidate !== 'object')
      return DEFAULT_UI_PREFERENCES;
    const legacyCardSize = (candidate as Partial<UiPreferences>).cardSize;
    return normalizeUiPreferences({
      ...candidate,
      cardSize:
        typeof legacyCardSize === 'number' &&
        legacyCardSize >= 1 &&
        legacyCardSize <= 7
          ? legacyCardSize + 2
          : legacyCardSize,
    });
  } catch {
    return DEFAULT_UI_PREFERENCES;
  }
}

export function useUiPreferences() {
  const [preferences, setPreferences] =
    useState<UiPreferences>(readPreferences);
  useEffect(() => {
    try {
      localStorage.setItem(UI_PREFERENCES_KEY, JSON.stringify(preferences));
    } catch {
      /* unavailable storage is non-fatal */
    }
  }, [preferences]);
  const setCardSize = useCallback(
    (cardSize: CardSize) =>
      setPreferences((current) => ({ ...current, cardSize })),
    [],
  );
  const setRemoveMargins = useCallback(
    (removeMargins: boolean) =>
      setPreferences((current) => ({ ...current, removeMargins })),
    [],
  );
  const setItemView = useCallback(
    (itemView: CollectionView) =>
      setPreferences((current) => ({ ...current, itemView })),
    [],
  );
  const setLocationView = useCallback(
    (locationView: CollectionView) =>
      setPreferences((current) => ({ ...current, locationView })),
    [],
  );
  const setItemSort = useCallback(
    (itemSort: ItemSort) =>
      setPreferences((current) => ({ ...current, itemSort })),
    [],
  );
  const setLocationSort = useCallback(
    (locationSort: LocationSort) =>
      setPreferences((current) => ({ ...current, locationSort })),
    [],
  );
  return {
    preferences,
    setCardSize,
    setRemoveMargins,
    setItemView,
    setLocationView,
    setItemSort,
    setLocationSort,
  };
}
